import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useProfiles } from "@/lib/profiles";
import { LayoutDashboard, FileText, Users, Building2, LogOut, ScrollText, Plus, ChevronDown, ShieldCheck, Menu, Wallet, ClipboardList, Truck, Briefcase, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const NavItem = ({ to, icon: Icon, label, testId, onNavigate }) => (
  <NavLink
    to={to}
    data-testid={testId}
    onClick={onNavigate}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 border-l-2 transition-colors font-sans text-sm ${
        isActive
          ? "border-[#D4AF37] bg-white/5 text-white"
          : "border-transparent text-neutral-500 hover:text-white hover:bg-white/[0.02]"
      }`
    }
  >
    <Icon className="w-4 h-4" strokeWidth={1.5} />
    {label}
  </NavLink>
);

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { profiles, activeProfileId, setActiveProfileId } = useProfiles();
  const active = profiles.find((p) => p.id === activeProfileId) || profiles[0];

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const go = (path) => {
    navigate(path);
    onNavigate?.();
  };

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A] text-neutral-200">
      <div className="px-6 py-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <ScrollText className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
          <span className="font-serif text-xl tracking-tight text-white">Ledger</span>
        </div>
        <p className="font-mont text-[9px] uppercase tracking-[0.3em] text-neutral-600 mt-2">
          Premium Invoicing
        </p>
      </div>

      {/* Active business switcher */}
      <div className="px-4 py-4 border-b border-white/10">
        <p className="font-mont text-[9px] uppercase tracking-[0.3em] text-neutral-500 mb-2">— Active Business</p>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="business-switcher"
              className="w-full flex items-center justify-between px-3 py-3 border border-white/10 hover:border-[#D4AF37]/50 transition-colors text-left"
            >
              <span className="font-serif text-sm text-white truncate">{active?.name || "Setup business"}</span>
              <ChevronDown className="w-4 h-4 text-neutral-500 flex-shrink-0 ml-2" strokeWidth={1.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-[#121212] border-white/10 rounded-none text-white min-w-[220px]">
            <DropdownMenuLabel className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">Switch business</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-white/10" />
            {profiles.map((p) => (
              <DropdownMenuItem
                key={p.id}
                data-testid={`switch-business-${p.name}`}
                onClick={() => setActiveProfileId(p.id)}
                className={`font-sans text-sm cursor-pointer rounded-none ${p.id === activeProfileId ? "text-[#D4AF37]" : "text-white"}`}
              >
                {p.name}
                {p.gst_number && <span className="ml-auto font-mont text-[9px] text-neutral-500">{p.gst_number}</span>}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem
              data-testid="manage-businesses"
              onClick={() => go("/profiles")}
              className="font-mont text-[10px] uppercase tracking-[0.2em] text-neutral-500 cursor-pointer rounded-none"
            >
              Manage businesses →
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="px-4 py-4">
        <Button
          data-testid="new-invoice-btn"
          onClick={() => go("/invoices/new")}
          className="w-full rounded-none bg-white text-black hover:bg-neutral-200 font-sans text-sm py-5"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
          New Invoice
        </Button>
      </div>

      <nav className="flex-1 py-2 overflow-y-auto">
        <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" onNavigate={onNavigate} />
        <NavItem to="/invoices" icon={FileText} label="Invoices" testId="nav-invoices" onNavigate={onNavigate} />
        <NavItem to="/quotations" icon={ClipboardList} label="Quotations" testId="nav-quotations" onNavigate={onNavigate} />
        <NavItem to="/finance" icon={Wallet} label="Finance" testId="nav-finance" onNavigate={onNavigate} />
        <NavItem to="/clients" icon={Users} label="Clients" testId="nav-clients" onNavigate={onNavigate} />
        <NavItem to="/vendors" icon={Truck} label="Vendors" testId="nav-vendors" onNavigate={onNavigate} />
        <NavItem to="/profiles" icon={Building2} label="Businesses" testId="nav-profiles" onNavigate={onNavigate} />
        {(user?.role === "admin" || user?.directors_access) && (
          <NavItem to="/letters" icon={Briefcase} label="Letters" testId="nav-letters" onNavigate={onNavigate} />
        )}
        <NavItem to="/receipts" icon={Receipt} label="Receipts" testId="nav-receipts" onNavigate={onNavigate} />
        {user?.role === "admin" && (
          <NavItem to="/admin" icon={ShieldCheck} label="Team" testId="nav-admin" onNavigate={onNavigate} />
        )}
      </nav>

      <div className="border-t border-white/10 p-4">
        <div className="flex items-center gap-3 mb-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/10" />
          ) : (
            <div className="w-8 h-8 bg-white/10 border border-white/10 flex items-center justify-center text-xs font-mont">
              {user?.name?.[0] || "U"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div data-testid="user-name" className="text-sm text-white truncate">{user?.name}</div>
            <div className="font-mont text-[10px] text-neutral-500 truncate">{user?.email}</div>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="flex items-center gap-2 text-xs font-mont uppercase tracking-[0.2em] text-neutral-500 hover:text-white transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200 flex flex-col lg:flex-row">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#0A0A0A]">
        <div className="flex items-center gap-2">
          <ScrollText className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
          <span className="font-serif text-lg tracking-tight text-white">Ledger</span>
        </div>
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setMobileOpen(true)}
          className="p-2 text-neutral-300 hover:text-white"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* Mobile slide-out drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-[#0A0A0A] border-white/10 [&>button]:text-white">
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-shrink-0 border-r border-white/10 bg-[#0A0A0A] sticky top-0 h-screen">
        <SidebarContent />
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
